import React, { ReactNode, useEffect, useState } from "react";
import { IXSifterCreditBalance, IXSifterEditingUser, IXSifterUser, IXSifterUserListing } from "../../types/XSifterAppUser";
import XSifterAdminService from "../../services/XSifterAdminService";
import { Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormControlLabel, FormGroup, Grid, IconButton, Input, InputAdornment, InputLabel, Pagination, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip } from "@mui/material";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import { IAdminUserListRequest } from "../../types/XSifterApiRequestTypes";
import { setDisplayMessage, setDisplayMessageType } from "../../store/XSifterAppReducer";
import { useAppDispatch } from "../../store/hooks";
import { Link, useNavigate } from "react-router";
import { formatIsoDate, formatNumberString, pluralizeCount } from "../../types/FormattingHelper";
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';
import { NumberField } from '@base-ui/react/number-field';
import SearchIcon from '@mui/icons-material/Search';
import SortableFieldHeader from "../common/SortableFieldHeader";
import { dir } from "console";

interface IXSifterAdminUserListProps {
    currentUser?: IXSifterUser;
    onForceHeaderRemount: () => void;
}

const XSifterAdminUserList: React.FC<IXSifterAdminUserListProps> = (props: IXSifterAdminUserListProps) => {
    const adminService = new XSifterAdminService();
    const [userList, setUserList] = useState<IXSifterUserListing | undefined>(undefined)
    const [listRequest, setListRequest] = useState<IAdminUserListRequest>({
        query: "",
        adminOnly: false,
        isNotApproved: false,
        disabledOnly: false,
        offset: 0,
        limit: 10,
        sortField: "created",
        sortDirection: "d"
    })
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(0);
    const itemsPerPage: number = 10;
    const [openEditUserDialog, setOpenEditUserDialog] = useState<boolean>(false);
    const [editingUser, setEditingUser] = useState<IXSifterEditingUser | undefined>(undefined);
    const [editingSaveEnabled, setEditingSaveEnabled] = useState<boolean>(false);
    const [openAddCreditsDialog, setOpenAddCreditsDialog] = useState<boolean>(false);
    const [addingCreditsUser, setAddingCreditsUser] = useState<IXSifterEditingUser | undefined>(undefined);
    const [addCreditsAmount, setAddCreditsAmount] = useState<number>(0);
    const [addCreditsBtnDisabled, setAddCreditsBtnDisabled] = useState<boolean>(true);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    useEffect(() => {
        refreshUserList(listRequest);
    }, []);

    const refreshUserList = (request: IAdminUserListRequest) => {
        adminService.getUserList(request, props.currentUser)
            .then((response: IXSifterUserListing) => {
                setUserListResponse(request, response);
            })
            .catch((error) => {
            });
    }

    const setUserListResponse = (request: IAdminUserListRequest, response: IXSifterUserListing) => {
        let totalItems = response.meta.count;
        let itemsPerPage = response.meta.limit;
        let currentOffset = response.meta.offset;
        if (totalItems === 0) {
            setTotalPages(0);
            setPageNumber(0);
        } else {
            let totalPages = Math.ceil(totalItems / itemsPerPage);
            let currentPage = Math.ceil(currentOffset / itemsPerPage) + 1;
            setTotalPages(totalPages);
            setPageNumber(currentPage);
        } 
        setListRequest({
            ...request,
            offset: response.meta.offset,
            limit: response.meta.limit
        })
        setUserList(response);
    }

    const handlePageChange =(event: React.ChangeEvent<unknown>, value: number) => {
        // TODO - change this hardcoded item
        let newOffset = (value-1) * 10; // 10 is hardcoded offset
        refreshUserList({
            ...listRequest,
            offset: newOffset
        });
    }

    const approveUser = (id: number) => {
        adminService.approveUser(id, props.currentUser)
            .then((response) => {
                dispatch(setDisplayMessage("User approved"));
                dispatch(setDisplayMessageType("success"));
                props.onForceHeaderRemount();
                refreshUserList(listRequest);
            }).catch((error) => {});
    }
    
    const formattedCreditBalance = (balance?: IXSifterCreditBalance): string => {
        if (!balance)
            return "";

        return `${formatNumberString(balance.ledgerBalance)} (${formatNumberString(balance.availableBalance)} available, ${formatNumberString(balance.reservedCredits)} reserved)`
    }

    const editUser = (userId: number) => {
        // get and set user to edit
        adminService.getUserInfo(userId, props.currentUser)
        .then((response) => {
            if (response) {
                setEditingUser(response);
                setEditingSaveEnabled(false);
                setOpenEditUserDialog(true);
            } else {
                setEditingUser(undefined);
                setEditingSaveEnabled(false);
                setOpenEditUserDialog(false);
            }
        }).catch(() => {
            setEditingUser(undefined);
            setEditingSaveEnabled(false);
            setOpenEditUserDialog(false);
        })
    }


    const onCloseEditUserDialog = (event?, reason?) => {
        if (reason && reason === 'backdropClick')
            return;

        setOpenEditUserDialog(false);
        setEditingUser(undefined);
    }

    const onEditUserSave = () => {
        if (!editingUser)
            return;

        adminService.getUserInfo(editingUser.id, props.currentUser)
            .then((response) => {
                let patchList: any[] = [];
                if (response?.isApproved !== editingUser.isApproved) {
                    patchList.push(
                        { path: "IsApproved", value: editingUser.isApproved.toString() }
                    );
                }
                if (response?.isAdmin !== editingUser.isAdmin) {
                    patchList.push({
                        path: "isAdmin", value: editingUser.isAdmin.toString()
                    });
                }
                if (response?.isDisabled !== editingUser.isDisabled) {
                    patchList.push({
                        path: "IsDisabled", value: editingUser.isDisabled.toString()
                    });
                }

                adminService.updateUser(editingUser.id, patchList, props.currentUser)
                    .then((response) => {
                        setOpenEditUserDialog(false);
                        setEditingUser(undefined);
                        dispatch(setDisplayMessage("User updated successfully"));
                        dispatch(setDisplayMessageType("success"));
                        props.onForceHeaderRemount();
                        refreshUserList(listRequest);
                    }).catch((error) => {
                        setOpenEditUserDialog(false);
                        setEditingUser(undefined);
                        dispatch(setDisplayMessage("Could not update user"));
                        dispatch(setDisplayMessageType("error"));
                    })
            }).catch(() => {
                setOpenEditUserDialog(false);
                setEditingUser(undefined);
                dispatch(setDisplayMessage("Could not update user"));
                dispatch(setDisplayMessageType("error"));
            });
    }

    const hasUserEditChanged = (updatedUser: IXSifterEditingUser): boolean => {
        if (!editingUser)
            return false;

        return (
            editingUser.isApproved !== updatedUser.isApproved ||
            editingUser.isDisabled !== updatedUser.isDisabled ||
            editingUser.isAdmin !== updatedUser.isAdmin
        );
    }

    const onUserApprovedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!editingUser)
            return;

        let editedUser = {
            ...editingUser,
            isApproved: event.target.checked
        };
        setEditingSaveEnabled(hasUserEditChanged(editedUser))
        setEditingUser(editedUser);
    }

    const onUserDisabledChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!editingUser)
            return;

        let editedUser = {
            ...editingUser,
            isDisabled: event.target.checked
        };
        setEditingSaveEnabled(hasUserEditChanged(editedUser))
        setEditingUser(editedUser);
    }

    const onUserIsAdminChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!editingUser)
            return;

        let editedUser = {
            ...editingUser,
            isAdmin: event.target.checked
        };
        setEditingSaveEnabled(hasUserEditChanged(editedUser))
        setEditingUser(editedUser);
    }

    const onClosedAddCreditsDialog = () => {
        setAddingCreditsUser(undefined);
        setOpenAddCreditsDialog(false);
        setAddCreditsAmount(0);
        setAddCreditsBtnDisabled(true);
    }

    const onAddUserCredits = (userId: number) => {
        adminService.getUserInfo(userId, props.currentUser)
        .then((response) => {
            if (response) {
                setAddingCreditsUser(response);
                setOpenAddCreditsDialog(true);
                setAddCreditsAmount(0);
                setAddCreditsBtnDisabled(true);
            } else {
                onClosedAddCreditsDialog();
            }
        }).catch(() => {
            onClosedAddCreditsDialog();
        })        
    }

    const onChangeAddCreditAmount = (value) => {
        setAddCreditsBtnDisabled((!value || value < 0));
        setAddCreditsAmount(value);
    }

    const onAddCredits = () => {
        if (!addingCreditsUser || !addCreditsAmount || addCreditsAmount < 0)
            return;

        adminService.addCreditsForUser(addingCreditsUser.id, addCreditsAmount, props.currentUser)
        .then((response) => {
            if (response) {
                dispatch(setDisplayMessage("Credits added successfully"));
                dispatch(setDisplayMessageType("success"));
                refreshUserList(listRequest);
            } else {
                dispatch(setDisplayMessage("Could not add credits. See log for details"));
                dispatch(setDisplayMessageType("warning"));
            }
            onClosedAddCreditsDialog();
        }).catch((error) => {
            dispatch(setDisplayMessage("Could not add credits. See log for details"));
            dispatch(setDisplayMessageType("warning"));
            onClosedAddCreditsDialog();
        });
    }

    const renderUserList = (): ReactNode => {
        if (userList === undefined || userList.items.length === 0) {
            return (
                <TableRow key={"noUsersFound"}>
                    <TableCell colSpan={6}>No users found</TableCell>
                </TableRow>
            );
        }

        const getUserListRowEditButton = (id: number, userId: string) => {
            if (!props.currentUser || userId === props.currentUser?.userId) {
                return (<Button className="actionButton" disabled>edit</Button>)
            }
            return (<Button className="actionButton" onClick={() => editUser(id)}>edit</Button>)
        }

        return userList.items.map((row) => {
            return (
            <TableRow key={row.id}>
                <TableCell>
                    {getUserListRowEditButton(row.id, row.userGuid)}
                </TableCell>
                <TableCell>{row.userName}</TableCell>
                <TableCell>{row.emailAddress}</TableCell>
                <TableCell>{row.firstName} {row.lastName}</TableCell>
                <TableCell>
                    <Tooltip title="Add credits">
                        <IconButton onClick={() => onAddUserCredits(row.id)}>
                            <AddToPhotosIcon />
                        </IconButton>
                    </Tooltip>
                    {formattedCreditBalance(row.creditBalance)} 
                </TableCell>
                <TableCell>
                    {row.isApproved ? <CheckCircleIcon sx={{ color: '#008000' }} /> :
                        <Tooltip title="approve user">
                            <IconButton sx={{ cursor: 'pointer' }} onClick={() => approveUser(row.id)}>
                                <BlockIcon sx={{ color: '#ff0000' }} />
                            </IconButton>
                        </Tooltip>
                    }
                </TableCell>
                <TableCell>
                    {row.isAdmin ? (<CheckCircleIcon sx={{ color: '#008000' }} />) : <></>}
                </TableCell>
            </TableRow>
            );
        }) as ReactNode;
    }

    const filterUserList = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        refreshUserList({
            ...listRequest,
            query: event?.target?.value ??""
        });
    }

    const sortUserList = (field: string, direction: "a"|"d") => {
        refreshUserList({
            ...listRequest,
            sortField: field,
            sortDirection: direction,
        });
    }

    return (
        <>
        <Grid container>
            <Grid size={9}>
                <h1>XSifter Users</h1>
            </Grid>
            <Grid size={3} sx={{ textAlign: "right" }}>
                <Button variant='outlined' onClick={() => { navigate("/app")}} sx={{ marginBottom: "16px;", marginTop: "16px"}}>
                    &laquo; back
                </Button>
            </Grid>
        </Grid>
        <Grid container>
            <Grid size={4}>
                <Box sx={{flexGrow: 0 }}>
                    <FormControl variant="standard" style={{ width: "90%"}}>
                        <InputLabel htmlFor={`search-query-input`}>
                            Filter users
                        </InputLabel>
                        <Input
                            id={`search-users-input`}
                            startAdornment={
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            }
                            onChange={filterUserList}
                        />
                    </FormControl>
                </Box>
            </Grid>
            <Grid size={8}>
            </Grid>
            <Grid size={7} component="div">
                    <FormGroup style={{ display: "inline-block" }}>
                        <FormControlLabel control={
                            <Switch value={listRequest.isNotApproved}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    refreshUserList({
                                        ...listRequest,
                                        isNotApproved: event.target.checked
                                    });
                                }}  />} 
                            label="only unapproved users" 
                        />
                        <FormControlLabel control={
                            <Switch value={listRequest.disabledOnly}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    refreshUserList({
                                        ...listRequest,
                                        disabledOnly: event.target.checked
                                    });
                                }}  />} 
                            label="only disabled users" 
                        />
                        <FormControlLabel control={
                            <Switch value={listRequest.adminOnly}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    refreshUserList({
                                        ...listRequest,
                                        adminOnly: event.target.checked
                                    });
                                }}  />} 
                            label="only admin users" 
                        />
                    </FormGroup>
            </Grid>
            <Grid size={3}>
                { totalPages > 0 && <Pagination count={totalPages} page={pageNumber} onChange={handlePageChange} /> }
            </Grid>
            <Grid size={2} component="div">
                {pluralizeCount(userList?.meta?.count, "result", "s")}
            </Grid>
        </Grid>
        <Divider />
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="User Listing">
                <TableHead>
                    <TableRow key={0}>
                        <TableCell></TableCell>
                        <SortableFieldHeader
                            currentField={listRequest.sortField} 
                            currentDirection={listRequest.sortDirection}
                            sortField="username"
                            onSort={sortUserList}
                        >Username</SortableFieldHeader>
                        <SortableFieldHeader
                            currentField={listRequest.sortField} 
                            currentDirection={listRequest.sortDirection}
                            sortField="email"
                            onSort={sortUserList}
                        >Email</SortableFieldHeader>
                        <SortableFieldHeader
                            currentField={listRequest.sortField} 
                            currentDirection={listRequest.sortDirection}
                            sortField="name"
                            onSort={sortUserList}
                        >Name</SortableFieldHeader>
                        <TableCell><strong>Credits</strong></TableCell>
                        <SortableFieldHeader
                            currentField={listRequest.sortField} 
                            currentDirection={listRequest.sortDirection}
                            sortField="approved"
                            onSort={sortUserList}
                        >Approved?</SortableFieldHeader>
                        <SortableFieldHeader
                            currentField={listRequest.sortField} 
                            currentDirection={listRequest.sortDirection}
                            sortField="isadmin"
                            onSort={sortUserList}
                        >Admin?</SortableFieldHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    { renderUserList() }
                </TableBody>
            </Table>
        </TableContainer>
        <Dialog open={openAddCreditsDialog} onClose={onClosedAddCreditsDialog}>
            <DialogTitle>
                Add credits: {addingCreditsUser?.firstName} {addingCreditsUser?.lastName}, ({addingCreditsUser?.userName})
            </DialogTitle>
            <DialogContent dividers>
                <FormControl>
                    <div className="editUserRow">
                        <strong>Email:</strong> {addingCreditsUser?.emailAddress}
                    </div>
                    <div className="editUserRow">
                        <strong>UserName:</strong> {addingCreditsUser?.userName}
                    </div>
                    <div className="editUserRow">
                        <strong>Name:</strong> {addingCreditsUser?.firstName} {addingCreditsUser?.lastName}
                    </div>
                    <Divider style={{ marginTop: "8px", marginBottom: "8px"}} />
                    <div className="editUserRow">
                        <strong>Credit Balance:</strong> {formattedCreditBalance(addingCreditsUser?.creditBalance)}
                    </div>
                    <Divider style={{ marginTop: "8px", marginBottom: "8px"}} />
                    <div className="editUserRow">
                        <TextField required variant="standard" label="Amount to add" value={addCreditsAmount} type="number" onChange={(e) => onChangeAddCreditAmount(e.target.value)} />
                    </div>
                </FormControl>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClosedAddCreditsDialog}>Cancel</Button>
                <Button onClick={onAddCredits} disabled={addCreditsBtnDisabled}>Add Credits</Button>
            </DialogActions>            
        </Dialog>
        <Dialog open={openEditUserDialog} onClose={onCloseEditUserDialog}>
            <DialogTitle>
                Edit User: {editingUser?.firstName} {editingUser?.lastName}, ({editingUser?.userName})
            </DialogTitle>
            <DialogContent dividers>
                {editingUser && (
                <FormControl>
                    <div className="editUserRow">
                        <strong>Email:</strong> {editingUser?.emailAddress}
                    </div>
                    <div className="editUserRow">
                        <strong>UserName:</strong> {editingUser?.userName}
                    </div>
                    <div className="editUserRow">
                        <strong>Name:</strong> {editingUser?.firstName} {editingUser?.lastName}
                    </div>
                    <Divider style={{ marginTop: "8px", marginBottom: "8px"}} />
                    <div className="editUserRow">
                        <strong>Approved?</strong> <Switch checked={editingUser?.isApproved} onChange={onUserApprovedChange} />
                    </div>
                    <div className="editUserRow">
                        <strong>Disabled?</strong> <Switch checked={editingUser?.isDisabled} onChange={onUserDisabledChange} />
                    </div>
                    <div className="editUserRow">
                        <strong>Is Admin?</strong> <Switch checked={editingUser?.isAdmin} onChange={onUserIsAdminChange} />
                    </div>
                    <Divider style={{ marginTop: "8px", marginBottom: "8px"}} />
                    <div className="editUserRow">
                        <strong>Credit Balance:</strong> {formattedCreditBalance(editingUser?.creditBalance)}
                    </div>
                    <Divider style={{ marginTop: "8px", marginBottom: "8px"}} />
                    <div className="editUserRow">
                        <strong>UserId:</strong> {editingUser?.userGuid}
                    </div>
                    <div>
                        <strong>Created:</strong> {formatIsoDate(editingUser?.creationDate)}
                        <br />
                        <strong>Last Login:</strong> {formatIsoDate(editingUser?.lastLoginDate)} (from {editingUser?.lastLoginIpAddress})
                    </div>
                    <div className="editUserRow">
                        <strong>DiscoverText Host:</strong> {editingUser?.hostname}
                    </div>
                </FormControl>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCloseEditUserDialog}>Cancel</Button>
                <Button onClick={onEditUserSave} disabled={!editingSaveEnabled}>Save</Button>
            </DialogActions>            
        </Dialog>
        </>
    );
};

export default XSifterAdminUserList;